import React, { useEffect, useState } from 'react'
import {
  Box,
  Typography,
  useTheme,
  alpha,
  Skeleton,
  Card,
  CardContent,
  Chip,
} from '@mui/material'
import { Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { backendClient } from '../../../api/backend'

const GOLD = '#c9a96e'

function formatValue(val, currency) {
  if (!val) return null
  const num = typeof val === 'number' ? val : parseFloat(val)
  if (isNaN(num) || num === 0) return null
  let formatted
  if (num >= 1_000_000) formatted = `${(num / 1_000_000).toFixed(1)}M`
  else if (num >= 1_000) formatted = `${(num / 1_000).toFixed(0)}k`
  else formatted = num.toLocaleString()
  return currency ? `${formatted} ${currency}` : formatted
}

const PartyIntelligence = () => {
  const { t, i18n } = useTranslation()
  const currentLang = i18n.language
  const theme = useTheme()
  const [loading, setLoading] = useState(true)
  const [topParties, setTopParties] = useState([])

  useEffect(() => {
    setLoading(true)
    backendClient
      .getDashboardTopCounterparties()
      .then((data) => setTopParties(data?.parties || []))
      .catch(() => setTopParties([]))
      .finally(() => setLoading(false))
  }, [])

  const cardSx = {
    elevation: 0,
    borderRadius: '12px',
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    boxShadow:
      theme.palette.mode === 'dark'
        ? '0 2px 8px rgba(0, 0, 0, 0.3)'
        : '0 2px 8px rgba(0, 0, 0, 0.04)',
    direction: currentLang === 'ar' ? 'rtl' : 'ltr',
    height: '100%',
  }

  return (
    <Card elevation={0} sx={cardSx}>
      <CardContent
        sx={{
          p: 0,
          '&:last-child': { pb: 0 },
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2,
            pt: 2,
            pb: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Users size={15} color={GOLD} />
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: theme.palette.text.primary, fontSize: '0.82rem' }}
          >
            {t('dashboard.partyIntelligence.topParties', 'Top Parties')}
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ px: 2, py: 1.5 }}>
            {Array.from({ length: 4 }).map((_, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
                <Skeleton variant="circular" width={28} height={28} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="50%" height={14} />
                </Box>
                <Skeleton variant="text" width={30} height={14} />
                <Skeleton variant="text" width={50} height={14} />
              </Box>
            ))}
          </Box>
        ) : topParties.length > 0 ? (
          <Box sx={{ flex: 1 }}>
            {/* Table header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 0.75,
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              }}
            >
              <Box sx={{ width: 16 }} />
              <Box sx={{ width: 28 }} />
              <Typography
                variant="caption"
                sx={{
                  flex: 1,
                  fontWeight: 600,
                  color: theme.palette.text.disabled,
                  fontSize: '0.62rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}
              >
                {t('dashboard.partyIntelligence.title', 'Party Intelligence')}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.disabled,
                  fontSize: '0.62rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  minWidth: 32,
                  textAlign: 'center',
                }}
              >
                {t('dashboard.partyIntelligence.contracts', 'Contracts')}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.disabled,
                  fontSize: '0.62rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  minWidth: 40,
                  textAlign: 'right',
                }}
              >
                {t('dashboard.partyIntelligence.value', 'Value')}
              </Typography>
            </Box>

            {/* Rows */}
            {topParties.slice(0, 6).map((party, index) => {
              const formattedValue = formatValue(party.total_value, party.currency)
              return (
                <Box
                  key={party.party_id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 2,
                    py: 0.75,
                    borderRadius: '8px',
                    transition: 'background 0.15s ease',
                    '&:hover': {
                      background: alpha(GOLD, 0.04),
                    },
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: index < 3 ? GOLD : theme.palette.text.secondary,
                      fontSize: '0.75rem',
                      width: 16,
                      textAlign: 'center',
                    }}
                  >
                    {index + 1}
                  </Typography>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      background: alpha(GOLD, 0.08),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Users size={14} color={GOLD} />
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      flex: 1,
                      fontWeight: 500,
                      color: theme.palette.text.primary,
                      fontSize: '0.8rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {party.name}
                  </Typography>
                  <Chip
                    label={party.contract_count}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      bgcolor: alpha(theme.palette.text.secondary, 0.08),
                      color: theme.palette.text.secondary,
                    }}
                  />
                  {formattedValue && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.secondary,
                        fontSize: '0.72rem',
                        whiteSpace: 'nowrap',
                        minWidth: 40,
                        textAlign: 'right',
                      }}
                    >
                      {formattedValue}
                    </Typography>
                  )}
                </Box>
              )
            })}
          </Box>
        ) : (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 160,
            }}
          >
            <Users size={24} color={theme.palette.text.disabled} />
            <Typography
              variant="body2"
              sx={{ mt: 1, color: theme.palette.text.disabled, fontSize: '0.78rem' }}
            >
              {t('dashboard.partyIntelligence.noParties', 'No party data yet')}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

export default PartyIntelligence
